/**
 * People being brought out: POWs, hostages, the captured officer. Once freed they jog at the
 * ranger's heels; blasts and enemy rounds can hit them; and when the Osprey hovers they go up
 * the ladder first, a second each, while the ranger covers them (02 §10).
 */

import { findPath } from '../astar';
import type { Ctx } from '../ctx';
import { nearestFree } from '../enemies/movement';
import { atan2 } from '../math';
import { resolveMovement, segmentHit } from '../statics';
import { EXPANSION, OBJECTIVES, RANGER } from '../tuning';
import { type Captive, DT, type World } from '../types';
import { dist, isPrisoner, loseCaptive } from './common';

/** A wound; the second one kills. */
function hurt(ctx: Ctx, c: Captive, lethal: boolean): void {
  if (lethal || c.wounded) loseCaptive(ctx, c);
  else c.wounded = true;
}

/** Held captives are under concrete or below ground; only people out in the open can be hit. */
function stepHarm(ctx: Ctx, exposed: Captive[]): void {
  const w = ctx.world;
  for (const ev of ctx.events) {
    if (ev.type !== 'explosion') continue;
    for (const c of exposed) {
      if (c.state !== 'following') continue;
      const d = dist(c.x, c.z, ev.x, ev.z);
      if (d <= ev.radius * 0.5) hurt(ctx, c, true);
      else if (d <= ev.radius) hurt(ctx, c, false);
    }
  }
  // Enemy rounds that crossed an escort this tick stop in him. (The projectile step has already
  // moved them; a round that also reached the ranger or a wall this tick is gone and cannot be seen.)
  for (let i = w.bullets.length - 1; i >= 0; i--) {
    const b = w.bullets[i];
    if (!b || b.fromPlayer) continue;
    for (const c of exposed) {
      if (c.state !== 'following') continue;
      const body = {
        propId: -1, shape: 'circle' as const, x: c.x, z: c.z, r: OBJECTIVES.captiveRadius, hx: 0, hz: 0, h: 2,
        blocksMove: false, blocksSight: false, blocksBullets: true,
      };
      if (!segmentHit(body, b.prevX, b.prevZ, b.x, b.z)) continue;
      w.bullets.splice(i, 1);
      ctx.events.push({ type: 'impact', x: c.x, z: c.z, y: 1.2 });
      if (ctx.rng.chance(OBJECTIVES.captiveWoundChance)) hurt(ctx, c, false);
      break;
    }
  }
}

function requestPath(ctx: Ctx, c: Captive, x: number, z: number): void {
  const start = nearestFree(ctx.statics, c.x, c.z, 2);
  const goal = nearestFree(ctx.statics, x, z, 4);
  const cells = start && goal ? findPath(ctx.statics.nav, start[0], start[1], goal[0], goal[1], 6000) : null;
  c.path = cells ? cells.map(([cx, cz]) => [cx + 0.5, cz + 0.5] as [number, number]) : [];
  c.pathIndex = c.path.length > 1 ? 1 : 0;
  // Paths are the expensive part: whatever came back, do not ask again for a while.
  c.stuckTicks = -OBJECTIVES.repathCooldownTicks;
}

/** Steer straight for the goal; only when that walks into something, fall back to a real path. */
function walkToward(ctx: Ctx, c: Captive, tx: number, tz: number, stop: number, others: Captive[]): void {
  const gap = dist(c.x, c.z, tx, tz);
  if (gap <= stop) {
    c.moving = false;
    c.path = [];
    c.stuckTicks = 0;
    if (gap > 0.05) c.facing = atan2(tx - c.x, tz - c.z);
    return;
  }
  let wx = tx;
  let wz = tz;
  const last = c.path[c.path.length - 1];
  // The ranger has moved on from where this path led: forget it and try the straight line again.
  if (last && dist(last[0], last[1], tx, tz) > 4) c.path = [];
  const node = c.path[c.pathIndex];
  if (c.path.length > 0 && node) {
    if (dist(c.x, c.z, node[0], node[1]) < 0.4) {
      c.pathIndex++;
      if (c.pathIndex >= c.path.length) c.path = [];
    } else {
      wx = node[0];
      wz = node[1];
    }
  }
  let dx = wx - c.x;
  let dz = wz - c.z;
  const d = Math.max(1e-6, Math.sqrt(dx * dx + dz * dz));
  dx /= d;
  dz /= d;
  // Keep a little room between escorts so the column does not collapse into one man.
  for (const o of others) {
    if (o === c || o.state !== 'following') continue;
    const sx = c.x - o.x;
    const sz = c.z - o.z;
    const sd = Math.sqrt(sx * sx + sz * sz);
    if (sd > 1e-4 && sd < OBJECTIVES.followSpacing) {
      dx += (sx / sd) * 0.6;
      dz += (sz / sd) * 0.6;
    }
  }
  // The pilot came down hard: he walks, never runs (10 §2, mission 27).
  const pace = c.kind === 'pilot' ? EXPANSION.pilotSpeed : OBJECTIVES.followSpeed;
  const speed = pace * (c.wounded ? OBJECTIVES.woundedSpeedFactor : 1);
  const step = Math.min(d, speed * DT);
  const moved = resolveMovement(ctx.statics, c.x + dx * step, c.z + dz * step, OBJECTIVES.captiveRadius);
  const gained = dist(c.x, c.z, moved.x, moved.z);
  c.x = moved.x;
  c.z = moved.z;
  c.travelled += gained;
  c.moving = gained > 1e-4;
  c.facing = atan2(dx, dz);
  if (gained < step * 0.3) c.stuckTicks++;
  else if (c.stuckTicks > 0) c.stuckTicks--;
  else if (c.stuckTicks < 0) c.stuckTicks++;
  // Stuck with no path, or stuck on a path that has gone bad (a prop fell across it): ask again.
  if (c.stuckTicks >= OBJECTIVES.stuckTicks) requestPath(ctx, c, tx, tz);
}

/** One man on the ladder at a time; the nearest waiting escort goes next. */
function stepLadder(ctx: Ctx, followers: Captive[]): void {
  const o = ctx.world.objective;
  if (ctx.world.osprey.phase !== 'hovering') {
    for (const c of followers) c.climbTicks = 0;
    return;
  }
  let climber = followers.find((c) => c.climbTicks > 0);
  if (!climber) {
    let bestD: number = RANGER.boardRadius;
    for (const c of followers) {
      const d = dist(c.x, c.z, o.pickupX, o.pickupZ);
      if (d <= bestD) {
        bestD = d;
        climber = c;
      }
    }
  }
  if (!climber) return;
  climber.climbTicks++;
  climber.moving = false;
  if (climber.climbTicks >= OBJECTIVES.captiveBoardTicks) {
    climber.state = 'boarded';
    climber.climbTicks = 0;
    climber.path = [];
    ctx.events.push({ type: 'captive', id: climber.id, what: 'boarded' });
  }
}

export function stepCaptives(ctx: Ctx): void {
  const w = ctx.world;
  const o = w.objective;
  const r = w.ranger;
  // Everyone's previous position, moving or not: the renderer interpolates from it.
  for (const c of o.captives) {
    c.prevX = c.x;
    c.prevZ = c.z;
  }
  const exposed = o.captives.filter((c) => c.state === 'following');
  if (exposed.length === 0) return;
  stepHarm(ctx, exposed);

  const hovering = w.osprey.phase === 'hovering';
  const rangerAtLadder = dist(r.x, r.z, o.pickupX, o.pickupZ) <= OBJECTIVES.ladderQueueRadius;
  let rank = 0;
  for (const c of exposed) {
    if (c.state !== 'following' || c.climbTicks > 0) continue;
    // POWs and hostages run for the ladder on their own; a prisoner only goes where his captor goes.
    const near = !isPrisoner(c.kind) && dist(c.x, c.z, o.pickupX, o.pickupZ) <= OBJECTIVES.ladderRushRadius;
    const rush = hovering && (rangerAtLadder || near);
    // The defector is no soldier: while rounds are flying he stays where he is - except for the ladder itself.
    if (c.kind === 'defector' && EXPANSION.defectorFreezes && w.combatTicks > 0 && !rush) c.moving = false;
    else if (rush) walkToward(ctx, c, o.pickupX, o.pickupZ, 0.5, exposed);
    else walkToward(ctx, c, r.x, r.z, OBJECTIVES.followGap + rank * OBJECTIVES.followSpacing, exposed);
    rank++;
  }
  stepLadder(ctx, exposed.filter((c) => c.state === 'following'));
}

/**
 * For the extraction rules in mission.ts. `blocking`: escorts are on or making for the ladder,
 * so the ranger waits his turn - unless the Osprey is about to leave. `climbing`: a man is on
 * the ladder, and the Osprey does not pull away from under him.
 */
export function escortsAtLadder(w: World): { blocking: boolean; climbing: boolean } {
  const o = w.objective;
  let queued = false;
  let climbing = false;
  if (w.osprey.phase === 'hovering') {
    for (const c of o.captives) {
      if (c.state !== 'following') continue;
      if (c.climbTicks > 0) climbing = true;
      if (dist(c.x, c.z, o.pickupX, o.pickupZ) <= OBJECTIVES.ladderQueueRadius) queued = true;
    }
  }
  return { blocking: climbing || (queued && w.osprey.ticks > OBJECTIVES.lastCallTicks), climbing };
}
